import { useState, useRef, useEffect } from "react";
import { Send, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { streamChat } from "@/lib/chatStream";

type Msg = { role: "user" | "assistant"; content: string };

const quickReplies = [
  "What services do you offer?",
  "How much does it cost?",
  "How fast can you set it up?",
];

const HeroChatDemo = () => {
  const [messages, setMessages] = useState<Msg[]>([
    { role: "assistant", content: "👋 Hey there! I'm Xentic's AI assistant. Ask me anything about our services, pricing, or setup process!" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const resetChat = () => {
    setMessages([{ role: "assistant", content: "👋 Hey there! I'm Xentic's AI assistant. Ask me anything about our services, pricing, or setup process!" }]);
    setConversationId(null);
    setInput("");
    setLoading(false);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text?: string) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    if (msg.length > 2000) {
      setMessages((p) => [...p, { role: "assistant", content: "Please keep your message under 2000 characters." }]);
      return;
    }
    setInput("");
    setMessages((p) => [...p, { role: "user", content: msg }]);
    setLoading(true);

    let assistantSoFar = "";
    const upsertAssistant = (chunk: string) => {
      assistantSoFar += chunk;
      const current = assistantSoFar;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && prev.length > 1 && prev[prev.length - 2]?.role === "user") {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: current } : m));
        }
        return [...prev, { role: "assistant", content: current }];
      });
    };

    try {
      await streamChat({
        message: msg,
        conversationId,
        onConversationId: (id) => setConversationId(id),
        onDelta: upsertAssistant,
        onDone: () => setLoading(false),
        onError: (errMsg) => {
          setMessages((p) => [...p, { role: "assistant", content: errMsg }]);
          setLoading(false);
        },
      });
    } catch {
      setMessages((p) => [...p, { role: "assistant", content: "Hmm, something went wrong. Try again or [book a consultation](/contact)." }]);
      setLoading(false);
    }
  };

  const showQuickReplies = messages.length <= 1 && !loading;

  return (
    <div className="glass-card rounded-2xl shadow-2xl overflow-hidden w-full max-w-md glow-shadow-sm">
      <div className="px-4 py-3 flex items-center justify-between border-b border-border" style={{ background: "linear-gradient(135deg, rgba(0,217,255,0.15), rgba(79,70,229,0.15))" }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center font-heading font-bold text-base text-white shadow-lg" style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}>X</div>
          <div className="flex flex-col">
            <span className="font-heading font-bold text-sm leading-tight text-foreground">Xentic AI Assistant</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" /> Online · Responding instantly</span>
          </div>
        </div>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={resetChat}
                aria-label="Reset chat"
                className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-md hover:bg-white/10"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p className="text-xs">New Chat</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Fixed height container with internal scroll */}
      <div ref={scrollRef} className="h-[320px] overflow-y-auto p-4 space-y-3">
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                  m.role === "user" ? "text-white" : "glass-card text-foreground"
                }`}
                style={m.role === "user" ? { background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" } : undefined}
              >
                <ReactMarkdown
                  components={{
                    a: ({ href, children }) => (
                      <button onClick={() => navigate(href || "/contact")} className="underline font-semibold text-primary">{children}</button>
                    ),
                  }}
                >
                  {m.content}
                </ReactMarkdown>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {loading && messages[messages.length - 1]?.role !== "assistant" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
            <div className="glass-card rounded-xl px-4 py-2 text-sm flex gap-1 items-center">
              <span className="w-2 h-2 rounded-full bg-primary animate-bounce" />
              <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0.15s" }} />
              <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0.3s" }} />
            </div>
          </motion.div>
        )}

        {/* Quick reply chips */}
        {showQuickReplies && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap gap-2 pt-1"
          >
            {quickReplies.map((q) => (
              <button
                key={q}
                onClick={() => send(q)}
                className="text-xs bg-primary/10 text-primary px-3 py-1.5 rounded-full hover:bg-primary/20 transition-colors font-medium border border-primary/20"
              >
                {q}
              </button>
            ))}
          </motion.div>
        )}
      </div>

      {/* CTA chip */}
      <div className="px-4 pb-2">
        <button
          onClick={() => navigate("/contact")}
          className="w-full text-xs text-primary px-3 py-2 rounded-lg hover:bg-primary/10 transition-colors font-medium text-center border border-primary/20 glass-card"
        >
          📞 Book Free Consultation
        </button>
      </div>

      <div className="border-t border-border p-3 flex gap-2">
        <label htmlFor="hero-chat-input" className="sr-only">Type your question</label>
        <input
          id="hero-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a question…"
          aria-label="Type your question"
          className="flex-1 bg-secondary rounded-lg px-3 py-2 text-sm outline-none placeholder:text-muted-foreground text-foreground border border-border focus:border-primary/50 transition-colors"
        />
        <Button
          size="icon"
          onClick={() => send()}
          disabled={loading || !input.trim()}
          aria-label="Send message"
          className="border-0"
          style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
        >
          <Send className="h-4 w-4 text-white" />
        </Button>
      </div>
    </div>
  );
};

export default HeroChatDemo;
